import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

const TourContext = createContext(null);

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

export const TourProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [steps, setSteps] = useState([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const startTour = useCallback((tourSteps) => {
    if (!tourSteps || tourSteps.length === 0) return;
    setSteps(tourSteps);
    setCurrentStepIndex(0);
    setIsOpen(true);
  }, []);

  // NEW: Ad-hoc trigger for a single item
  const showSpotlight = useCallback(({ targetId, title, content, placement = 'bottom' }) => {
    if (!targetId) return;
    setSteps([{ targetId, title, content, placement }]);
    setCurrentStepIndex(0);
    setIsOpen(true);
  }, []);

  const closeTour = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => {
        setSteps([]);
        setCurrentStepIndex(0);
    }, 300); 
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStepIndex((prev) => {
      if (prev >= steps.length - 1) {
        closeTour();
        return prev;
      }
      return prev + 1;
    });
  }, [steps.length, closeTour]);

  const prevStep = useCallback(() => {
    setCurrentStepIndex((prev) => (prev > 0 ? prev - 1 : 0));
  }, []);

  const value = useMemo(() => ({
    isOpen,
    steps,
    currentStepIndex,
    currentStep: steps[currentStepIndex],
    startTour,
    showSpotlight, // Exported here
    closeTour,
    nextStep,
    prevStep,
    isLastStep: currentStepIndex === steps.length - 1,
    isFirstStep: currentStepIndex === 0,
    isSingleStep: steps.length === 1, // Useful for UI conditional rendering
  }), [isOpen, steps, currentStepIndex, startTour, showSpotlight, closeTour, nextStep, prevStep]);

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
};

TourProvider.propTypes = {
  children: PropTypes.node.isRequired,
};